import { LmsAnalyticsData } from './lms-analytics.data';

// API Response Type
export interface EnrollmentApiData {
  Status?: string;
  EnrollmentId: number;
  LearningModuleId: number;
  UserId: number;
  EndLine?: number;
  LearnerName?: string;
  LastAccessedOn?: string;
  CSAT?: number;
  LearningModule?: string;
  BaseLine?: number;
  ModuleStartDate?: string;
  ModuleEndDate?: string;
  Progress?: number; // Progress percentage from API (capital P)
}

export interface LessonPlanApiData {
  id: number;
  name: string;
  code?: string;
  info?: {
    competencyWeights?: Record<string, number>;
  };
}

export interface TagApiData {
  id: number;
  name: string;
  displayLabel?: string;
}

export interface LearnerTopicAnalysisData {
  id: number;
  enquiryId: number;
  userId: number;
  learnerName: string;
  learnerCode: string;
  batchId: number;
  batchName: string;
  batchCode: string;
  tagName: string; // Competency/topic name
  testCategoryId: number;
  tagId: number; // Competency/topic ID
  score: number | null;
  totalMarksForQuestions: number;
  percentage: number | null;
  exposedQuestions: number;
  corrects: number;
  createdAt: number;
}

/**
 * Transforms API data from /edusquares/api/common/lms-admin-dashboard
 * /edusquares/api/common/lesson-plans/active, and /edusquares/api/common/tags/by-ids
 * into the LmsAnalyticsData format required by the dashboard
 */
export const adaptApiDataToDashboard = (
  apiData: EnrollmentApiData[],
  lessonPlansData: LessonPlanApiData[] = [],
  tagsData: TagApiData[] = [],
  baselineData: LearnerTopicAnalysisData[] = [],
  endlineData: LearnerTopicAnalysisData[] = []
): LmsAnalyticsData => {
  // Calculate statistics
  const validEnrollments = apiData.filter(e => e.LearningModule && e.Status);

  // Unique learners
  const uniqueLearners = new Set(validEnrollments.map(e => e.UserId)).size;

  // Status counts
  const statusCounts = validEnrollments.reduce((acc, e) => {
    acc[e.Status || 'UNKNOWN'] = (acc[e.Status || 'UNKNOWN'] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  const completed = statusCounts['COMPLETED'] || 0;
  const started = statusCounts['STARTED'] || 0;
  const assigned = statusCounts['ASSIGNED'] || 0;
  const paused = statusCounts['PAUSED_FOR_PAYMENT'] || 0;

  // Learning status donut: [Completed, In Progress (Started), Not Started (Assigned), Paused]
  const learningDonut = [completed, started, assigned, paused];

  // Overdue learners: those who haven't completed by ModuleEndDate
  const now = new Date();
  const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);

  const overdueEnrollments = validEnrollments.filter(e => {
    // Overdue = Not completed AND past the end date
    if (e.Status === 'COMPLETED') return false;
    if (!e.ModuleEndDate) return false;

    const endDate = new Date(e.ModuleEndDate);
    return endDate < now;
  });

  // Enhanced At-Risk learners: Include both overdue AND approaching deadline
  // Step 1: Identify at-risk enrollments
  const atRiskEnrollments = validEnrollments.filter(e => {
    if (!e.ModuleEndDate) return false;
    if (e.Status === 'COMPLETED') return false; // Exclude completed

    const endDate = new Date(e.ModuleEndDate);
    const daysUntilDeadline = Math.floor((endDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));

    // Include if: overdue OR approaching deadline (within 7 days) with low progress
    const isOverdue = endDate < now;
    const isApproachingDeadline = daysUntilDeadline > 0 && daysUntilDeadline <= 7;

    // For approaching deadlines, only include if progress is significantly behind
    if (isApproachingDeadline) {
      const actualProgress = e.Progress ?? 0;
      const expectedProgress = 70; // Should be at 70% within 7 days of deadline
      return actualProgress < expectedProgress;
    }

    return isOverdue;
  });

  // Step 2: Calculate comprehensive risk data for each enrollment
  const risks = atRiskEnrollments
    .map(e => {
      const endDate = new Date(e.ModuleEndDate!);
      const daysUntilDeadline = Math.floor((endDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
      const daysOverdue = daysUntilDeadline < 0 ? Math.abs(daysUntilDeadline) : undefined;
      const isOverdue = daysUntilDeadline < 0;

      // Check if also inactive (haven't accessed in 7 days)
      const isInactive = e.LastAccessedOn
        ? new Date(e.LastAccessedOn) < sevenDaysAgo
        : true;

      const flags = [];
      if (isInactive) flags.push('Inactive');

      // Calculate expected progress: (today - start) / (end - start) × 100
      let expectedProgress = 100;
      if (e.ModuleStartDate && e.ModuleEndDate) {
        const startDate = new Date(e.ModuleStartDate);
        const totalDuration = endDate.getTime() - startDate.getTime();
        const elapsed = now.getTime() - startDate.getTime();
        expectedProgress = totalDuration > 0 ? Math.min(Math.round((elapsed / totalDuration) * 100), 100) : 100;
      }

      // Use actual progress from API
      let actualProgress = e.Progress ?? 0;
      if (actualProgress === 0 && e.Progress === undefined) {
        if (e.Status === 'COMPLETED') actualProgress = 100;
        else if (e.Status === 'STARTED' || e.Status === 'INPROGRESS') actualProgress = 50;
        else if (e.Status === 'ASSIGNED') actualProgress = 0;
        else if (e.Status === 'PAUSED_FOR_PAYMENT') actualProgress = 25;
      }

      const progressGap = expectedProgress - actualProgress;

      // Calculate composite RISK SCORE (0-100, higher = more urgent)
      let riskScore = 0;

      // Factor 1: Overdue penalty (0-40 points)
      if (daysOverdue) {
        riskScore += Math.min(40, daysOverdue * 2); // 2 points per day overdue, max 40
      } else {
        // Approaching deadline (0-20 points based on proximity)
        const approachingScore = Math.max(0, (7 - daysUntilDeadline) * 3);
        riskScore += approachingScore;
      }

      // Factor 2: Progress gap (0-30 points)
      riskScore += Math.min(30, progressGap * 0.5); // 0.5 point per % behind

      // Factor 3: Inactivity (0-20 points)
      if (isInactive) {
        riskScore += 20;
      }

      // Factor 4: ROI potential - deduct for learners close to completion (0-10 points off)
      // High progress learners are worth pushing (lower risk score = higher priority in sorted list)
      if (actualProgress >= 80) {
        riskScore -= 10; // Boost priority for high completers
      } else if (actualProgress >= 60) {
        riskScore -= 5;
      }

      riskScore = Math.max(0, Math.min(100, Math.round(riskScore)));

      // Determine RISK CATEGORY
      let riskCategory: 'critical' | 'high' | 'medium' | 'approaching' = 'medium';
      if (daysOverdue && daysOverdue > 14) {
        riskCategory = 'critical';
      } else if (daysOverdue && daysOverdue > 7) {
        riskCategory = 'high';
      } else if (daysOverdue) {
        riskCategory = 'medium';
      } else {
        riskCategory = 'approaching';
      }

      // Determine CONTEXTUAL ACTION NEEDED
      let actionNeeded = '';
      if (e.Status === 'PAUSED_FOR_PAYMENT') {
        actionNeeded = 'Resolve payment issue';
      } else if (e.Status === 'COMPLETED') {
        actionNeeded = 'Completed ✓';
      } else if (daysOverdue && daysOverdue > 21) {
        actionNeeded = 'Critical: 21+ days overdue';
      } else if (daysOverdue && daysOverdue > 14) {
        actionNeeded = 'Urgent: Complete overdue course';
      } else if (daysOverdue && daysOverdue > 7) {
        actionNeeded = 'Overdue: Extension or completion';
      } else if (daysOverdue && daysOverdue > 0) {
        actionNeeded = actualProgress >= 80 ? 'Almost done - push to finish' : 'Overdue: Catch up needed';
      } else if (daysUntilDeadline <= 3 && actualProgress < 80) {
        actionNeeded = 'Urgent: Deadline in 3 days';
      } else if (daysUntilDeadline <= 7 && actualProgress < 50) {
        actionNeeded = 'Warning: Behind schedule';
      } else if (isInactive) {
        actionNeeded = 'Re-engage learner';
      } else {
        actionNeeded = 'Monitor progress';
      }

      return {
        learner: e.LearnerName || 'Unknown',
        course: e.LearningModule || 'Unknown Course',
        progress: actualProgress,
        expected: expectedProgress,
        last: e.LastAccessedOn || 'No access',
        flags,
        daysOverdue,
        daysUntilDeadline: daysUntilDeadline > 0 ? daysUntilDeadline : undefined,
        riskScore,
        riskCategory,
        actionNeeded,
        deadline: e.ModuleEndDate || ''
      };
    })
    .sort((a, b) => b.riskScore - a.riskScore); // Sort by risk score (highest risk first)

  // Get unique courses/modules
  const uniqueCourses = Array.from(new Set(validEnrollments.map(e => e.LearningModule).filter(Boolean))) as string[];

  // Calculate monthly enrollments (group by ModuleStartDate)
  const monthlyEnrollments: Record<string, number> = {};
  validEnrollments.forEach(e => {
    if (e.ModuleStartDate) {
      const date = new Date(e.ModuleStartDate);
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`; // YYYY-MM format
      monthlyEnrollments[monthKey] = (monthlyEnrollments[monthKey] || 0) + 1;
    }
  });

  // Get last 6 months from today
  const last6Months: string[] = [];
  const last6MonthLabels: string[] = [];
  for (let i = 5; i >= 0; i--) {
    const d = new Date();
    d.setMonth(d.getMonth() - i);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    const label = d.toLocaleString('en-US', { month: 'short', year: '2-digit' });
    last6Months.push(key);
    last6MonthLabels.push(label);
  }

  const enrollmentsMonthly = {
    labels: last6MonthLabels,
    values: last6Months.map(m => monthlyEnrollments[m] || 0)
  };

  // Deadlines - using ModuleEndDate from API
  // Get upcoming deadlines (within next 7 days)
  const today = new Date();
  const sevenDaysFromNow = new Date(today.getTime() + 7 * 24 * 60 * 60 * 1000);

  // Group by course, start date, and end date (cohort identifier)
  const deadlinesMap = new Map<string, { course: string; cohort: string; end: string; count: number; endDate: Date }>();

  validEnrollments.forEach(e => {
    if (e.ModuleEndDate && e.ModuleStartDate && e.Status === 'STARTED') {
      const endDate = new Date(e.ModuleEndDate);
      if (endDate >= today && endDate <= sevenDaysFromNow) {
        const key = `${e.LearningModule}_${e.ModuleStartDate}_${e.ModuleEndDate}`;
        if (deadlinesMap.has(key)) {
          const existing = deadlinesMap.get(key)!;
          existing.count++;
        } else {
          // Format cohort as "Mon YY" from start date
          const startDate = new Date(e.ModuleStartDate);
          const cohortLabel = startDate.toLocaleString('en-US', { month: 'short', year: '2-digit' });

          deadlinesMap.set(key, {
            course: e.LearningModule || 'Unknown',
            cohort: cohortLabel,
            end: e.ModuleEndDate.split(' ')[0], // Just the date part
            count: 1,
            endDate
          });
        }
      }
    }
  });

  const deadlinesRows = Array.from(deadlinesMap.values())
    .sort((a, b) => a.endDate.getTime() - b.endDate.getTime())
    .map(({ course, cohort, end, count }) => ({
      course,
      cohort,
      end,
      count
    }));

  // Competency Heatmap from Lesson Plans API
  // IMPORTANT: Only include lesson plans that have enrollments (i.e., filtered by analytics_dashboard=1)
  // Get unique lesson plan IDs from enrollments (these are already filtered by analytics_dashboard=1)
  const enrolledLessonPlanIds = new Set(validEnrollments.map(e => e.LearningModuleId));

  // Create tag map: id -> name
  const tagMap = new Map<string, string>();
  tagsData.forEach(tag => {
    tagMap.set(String(tag.id), tag.displayLabel || tag.name);
  });

  // Extract competency IDs and weights from lesson plans
  const competencyMap = new Map<string, string>(); // id -> name
  const lessonPlanMap = new Map<string, Record<string, number>>(); // lesson plan name -> { competencyId: weight }

  lessonPlansData.forEach(lp => {
    // FILTER: Only include lesson plans that have enrollments with analytics_dashboard=1
    if (lp.info?.competencyWeights && enrolledLessonPlanIds.has(lp.id)) {
      lessonPlanMap.set(lp.name, lp.info.competencyWeights);

      // Collect all competency IDs (tagIds) with their names
      Object.keys(lp.info.competencyWeights).forEach(compId => {
        if (!competencyMap.has(compId)) {
          const tagName = tagMap.get(compId) || `Tag ${compId}`;
          competencyMap.set(compId, tagName);
        }
      });
    }
  });

  // Get unique competencies and lesson plans with weights
  const competencies = Array.from(competencyMap.values());
  const competencyIds = Array.from(competencyMap.keys());
  const lessonPlansWithWeights = Array.from(lessonPlanMap.entries()).slice(0, 6); // Top 6 lesson plans

  const courses = lessonPlansWithWeights.map(([name]) =>
    name.length > 20 ? name.substring(0, 17) + '...' : name
  );

  // Build competency weights matrix (competencies x courses)
  const competencyWeights = competencyIds.map(compId =>
    lessonPlansWithWeights.map(([, weights]) => weights[compId] || 0)
  );

  // NO MOCK DATA - If no competency data exists, return empty arrays
  // The UI will handle showing "no data available" message

  // Pre/Post test scores (from BaseLine and EndLine)
  const completedWithScores = validEnrollments.filter(e =>
    e.Status === 'COMPLETED' && e.BaseLine !== undefined && e.EndLine !== undefined
  );

  const avgBaseline = completedWithScores.length > 0
    ? Math.round(completedWithScores.reduce((sum, e) => sum + (e.BaseLine || 0), 0) / completedWithScores.length)
    : 60;

  const avgEndline = completedWithScores.length > 0
    ? Math.round(completedWithScores.reduce((sum, e) => sum + (e.EndLine || 0), 0) / completedWithScores.length)
    : 75;

  const prePost = { pre: avgBaseline, post: avgEndline };

  // CSAT (average from completed courses)
  const completedWithCSAT = validEnrollments.filter(e => e.Status === 'COMPLETED' && e.CSAT !== undefined);
  const avgCSAT = completedWithCSAT.length > 0
    ? Math.round(completedWithCSAT.reduce((sum, e) => sum + (e.CSAT || 0), 0) / completedWithCSAT.length)
    : 0;

  // Effectiveness by course
  const effectivenessByCourse = uniqueCourses.slice(0, 4).map(course => {
    const courseData = validEnrollments.filter(e => e.LearningModule === course && e.Status === 'COMPLETED');

    const pre = courseData.length > 0 && courseData.some(e => e.BaseLine !== undefined)
      ? Math.round(courseData.reduce((sum, e) => sum + (e.BaseLine || 0), 0) / courseData.length)
      : 60;

    const post = courseData.length > 0 && courseData.some(e => e.EndLine !== undefined)
      ? Math.round(courseData.reduce((sum, e) => sum + (e.EndLine || 0), 0) / courseData.length)
      : 75;

    const csat = courseData.length > 0 && courseData.some(e => e.CSAT !== undefined)
      ? Math.round(courseData.reduce((sum, e) => sum + (e.CSAT || 0), 0) / courseData.filter(e => e.CSAT).length)
      : 0;

    return { course, pre, post, csat };
  });

  // Active programs count - unique by LearningModuleId + Start/End Date combination
  // This counts each "run" or "instance" of a program
  const activeProgramInstances = new Map<string, any>();

  validEnrollments.forEach(e => {
    if (e.LearningModuleId && e.ModuleStartDate && e.ModuleEndDate) {
      const key = `${e.LearningModuleId}_${e.ModuleStartDate}_${e.ModuleEndDate}`;
      if (!activeProgramInstances.has(key)) {
        activeProgramInstances.set(key, {
          LearningModuleId: e.LearningModuleId,
          LearningModule: e.LearningModule || 'Unknown',
          ModuleStartDate: e.ModuleStartDate,
          ModuleEndDate: e.ModuleEndDate,
          enrollments: [e],
          totalEnrollments: 1,
          completedEnrollments: e.Status === 'COMPLETED' ? 1 : 0,
          startedEnrollments: e.Status === 'STARTED' ? 1 : 0,
          assignedEnrollments: e.Status === 'ASSIGNED' ? 1 : 0
        });
      } else {
        const existing = activeProgramInstances.get(key)!;
        existing.enrollments.push(e);
        existing.totalEnrollments++;
        if (e.Status === 'COMPLETED') existing.completedEnrollments++;
        if (e.Status === 'STARTED') existing.startedEnrollments++;
        if (e.Status === 'ASSIGNED') existing.assignedEnrollments++;
      }
    }
  });

  // Calculate program cohort status based on start/end dates
  let programsCompleted = 0;  // Past end date
  let programsInProgress = 0;  // Between start and end date
  let programsNotStarted = 0;  // Before start date

  activeProgramInstances.forEach(program => {
    const startDate = new Date(program.ModuleStartDate);
    const endDate = new Date(program.ModuleEndDate);

    if (endDate < now) {
      // Program ended
      programsCompleted++;
    } else if (startDate <= now && endDate >= now) {
      // Program is currently running
      programsInProgress++;
    } else if (startDate > now) {
      // Program hasn't started yet
      programsNotStarted++;
    }
  });

  const programDonut = [programsCompleted, programsInProgress, programsNotStarted];

  const activePrograms = activeProgramInstances.size;
  const activeProgramRecords = Array.from(activeProgramInstances.values()).map(p => ({
    LearningModuleId: p.LearningModuleId,
    LearningModule: p.LearningModule,
    ModuleStartDate: p.ModuleStartDate,
    ModuleEndDate: p.ModuleEndDate,
    TotalEnrollments: p.totalEnrollments,
    CompletedEnrollments: p.completedEnrollments,
    StartedEnrollments: p.startedEnrollments,
    AssignedEnrollments: p.assignedEnrollments
  }));

  // Completion rates by cohort
  // Filter to cohorts with meaningful activity (at least some completions or significant enrollment)
  // Sort by completion rate to show best performing cohorts
  const sortedCohorts = activeProgramRecords
    .filter(c => c.TotalEnrollments >= 3) // At least 3 enrollments to be meaningful
    .filter(c => (c.CompletedEnrollments + c.StartedEnrollments) > 0) // Must have some activity
    .sort((a, b) => {
      // Sort by completion rate (highest first)
      const rateA = a.TotalEnrollments > 0 ? a.CompletedEnrollments / a.TotalEnrollments : 0;
      const rateB = b.TotalEnrollments > 0 ? b.CompletedEnrollments / b.TotalEnrollments : 0;
      return rateB - rateA;
    })
    .slice(0, 5);

  const completionRates = {
    labels: sortedCohorts.map(c => {
      const startDate = new Date(c.ModuleStartDate).toLocaleDateString('en-US', { month: 'short' });
      // Shorten course name if too long
      const courseName = c.LearningModule.length > 20
        ? c.LearningModule.substring(0, 17) + '...'
        : c.LearningModule;
      return `${courseName} ${startDate}`;
    }),
    values: sortedCohorts.map(c =>
      c.TotalEnrollments > 0 ? Math.round((c.CompletedEnrollments / c.TotalEnrollments) * 100) : 0
    )
  };

  // Upcoming deadlines count - from actual ModuleEndDate
  const upcomingDeadlines = deadlinesRows.reduce((sum, row) => sum + row.count, 0);

  // Get unique learner records
  const uniqueLearnerIds = Array.from(new Set(validEnrollments.map(e => e.UserId)));
  const uniqueLearnerRecords = uniqueLearnerIds.map(userId => {
    const enrollment = validEnrollments.find(e => e.UserId === userId);
    return {
      UserId: userId,
      LearnerName: enrollment?.LearnerName || 'Unknown',
      TotalEnrollments: validEnrollments.filter(e => e.UserId === userId).length
    };
  });

  // Get upcoming deadline records
  const upcomingDeadlineRecords = validEnrollments.filter(e => {
    if (e.ModuleEndDate && e.Status === 'STARTED') {
      const endDate = new Date(e.ModuleEndDate);
      return endDate >= today && endDate <= sevenDaysFromNow;
    }
    return false;
  });

  // Get completed, started, assigned records
  const completedRecords = validEnrollments.filter(e => e.Status === 'COMPLETED');
  const startedRecords = validEnrollments.filter(e => e.Status === 'STARTED');
  const assignedRecords = validEnrollments.filter(e => e.Status === 'ASSIGNED');

  // Program health index - composite score aggregated at program level
  // Factors: completion rate + on-time completion + engagement + low drop-off

  // First calculate health for each cohort
  const cohortHealthScores = Array.from(activeProgramInstances.values()).map(cohort => {
    const total = cohort.totalEnrollments;
    const completed = cohort.completedEnrollments;
    const started = cohort.startedEnrollments;

    // Factor 1: Completion rate (0-40 points)
    const completionRate = total > 0 ? (completed / total) : 0;
    const completionScore = completionRate * 40;

    // Factor 2: On-time completion - completed before end date (0-30 points)
    const endDate = new Date(cohort.ModuleEndDate);
    const completedOnTime = cohort.enrollments.filter((e: any) =>
      e.Status === 'COMPLETED' && (!e.LastAccessedOn || new Date(e.LastAccessedOn) <= endDate)
    ).length;
    const onTimeScore = completed > 0 ? (completedOnTime / completed) * 30 : 0;

    // Factor 3: Engagement - % that at least started (0-20 points)
    const engagementRate = total > 0 ? ((started + completed) / total) : 0;
    const engagementScore = engagementRate * 20;

    // Factor 4: Low drop-off - inverse of assigned-only rate (0-10 points)
    const assignedOnly = cohort.assignedEnrollments;
    const dropOffRate = total > 0 ? (assignedOnly / total) : 0;
    const retentionScore = (1 - dropOffRate) * 10;

    // Total health score (0-100)
    const healthScore = Math.round(completionScore + onTimeScore + engagementScore + retentionScore);

    return {
      program: cohort.LearningModule,
      score: healthScore,
      enrollments: total
    };
  });

  // Aggregate by program (weighted average by enrollment count)
  const programHealthMap = new Map<string, { totalScore: number; totalEnrollments: number }>();

  cohortHealthScores.forEach(({ program, score, enrollments }) => {
    if (programHealthMap.has(program)) {
      const existing = programHealthMap.get(program)!;
      existing.totalScore += score * enrollments; // Weighted
      existing.totalEnrollments += enrollments;
    } else {
      programHealthMap.set(program, {
        totalScore: score * enrollments,
        totalEnrollments: enrollments
      });
    }
  });

  // Calculate weighted average for each program
  const programHealthScores = Array.from(programHealthMap.entries()).map(([program, data]) => ({
    program,
    score: Math.round(data.totalScore / data.totalEnrollments),
    enrollments: data.totalEnrollments
  }));

  // Filter out programs with score of 0 (no activity at all)
  const programsWithActivity = programHealthScores.filter(p => p.score > 0);

  // Sort by score and get top 5 and bottom 5
  const sortedByScore = [...programsWithActivity].sort((a, b) => b.score - a.score);

  // Only show bottom 5 if there are more than 5 programs total
  let healthPrograms;
  if (sortedByScore.length <= 5) {
    // If 5 or fewer programs, just show all of them (all are "top")
    healthPrograms = sortedByScore;
  } else {
    const top5 = sortedByScore.slice(0, 5);
    const bottom5 = sortedByScore.slice(-5).reverse(); // Worst first

    // Remove duplicates (can happen if there are between 6-9 programs)
    const top5Ids = new Set(top5.map(p => p.program));
    const uniqueBottom5 = bottom5.filter(p => !top5Ids.has(p.program));

    healthPrograms = [...top5, ...uniqueBottom5];
  }

  const programHealth = {
    labels: healthPrograms.map(p => {
      const label = p.program.length > 20 ? p.program.substring(0, 17) + '...' : p.program;
      return label;
    }),
    values: healthPrograms.map(p => p.score),
    isTopPerformer: healthPrograms.map((p, i) => {
      // If total programs <= 5, all are top performers
      if (sortedByScore.length <= 5) return true;
      // Otherwise, first 5 are top performers
      return i < 5;
    })
  };

  // LEARNER COMPETENCY SKILL GAP ANALYSIS WITH BASELINE AND ENDLINE
  // Build learner × competency matrices for both baseline and endline
  const learnerBaselineMap = new Map<string, Map<string, { percentage: number; score: number; total: number }>>();
  const learnerEndlineMap = new Map<string, Map<string, { percentage: number; score: number; total: number }>>();

  // Process baseline data (pre-test)
  baselineData.forEach(item => {
    const learnerKey = `${item.userId}_${item.learnerName}`;
    if (!learnerBaselineMap.has(learnerKey)) {
      learnerBaselineMap.set(learnerKey, new Map());
    }

    const competencyMap = learnerBaselineMap.get(learnerKey)!;
    const competencyKey = item.tagName;

    if (item.percentage !== null) {
      competencyMap.set(competencyKey, {
        percentage: item.percentage,
        score: item.score || 0,
        total: item.totalMarksForQuestions
      });
    }
  });

  // Process endline data (post-test)
  endlineData.forEach(item => {
    const learnerKey = `${item.userId}_${item.learnerName}`;
    if (!learnerEndlineMap.has(learnerKey)) {
      learnerEndlineMap.set(learnerKey, new Map());
    }

    const competencyMap = learnerEndlineMap.get(learnerKey)!;
    const competencyKey = item.tagName;

    if (item.percentage !== null) {
      competencyMap.set(competencyKey, {
        percentage: item.percentage,
        score: item.score || 0,
        total: item.totalMarksForQuestions
      });
    }
  });

  // Get all unique competencies from both baseline and endline data
  const allCompetencies = Array.from(new Set([
    ...baselineData.map(item => item.tagName),
    ...endlineData.map(item => item.tagName)
  ]));

  // Build learner skill matrix with baseline and endline comparison
  // Get all unique learners from both datasets
  const allLearners = new Set([
    ...Array.from(learnerBaselineMap.keys()),
    ...Array.from(learnerEndlineMap.keys())
  ]);

  const learnerSkillMatrix = Array.from(allLearners).map(learnerKey => {
    const [userId, learnerName] = learnerKey.split('_');
    const baselineScores = learnerBaselineMap.get(learnerKey);
    const endlineScores = learnerEndlineMap.get(learnerKey);

    const competencyPerformance = allCompetencies.map(comp => {
      const baseline = baselineScores?.get(comp);
      const endline = endlineScores?.get(comp);

      // Calculate learning gain
      const learningGain = (baseline?.percentage !== null && baseline?.percentage !== undefined) &&
                          (endline?.percentage !== null && endline?.percentage !== undefined)
        ? endline.percentage - baseline.percentage
        : null;

      return {
        competency: comp,
        baselinePercentage: baseline?.percentage ?? null,
        endlinePercentage: endline?.percentage ?? null,
        percentage: endline?.percentage ?? baseline?.percentage ?? null, // Use endline if available, else baseline
        learningGain,
        score: endline?.score ?? baseline?.score ?? null,
        total: endline?.total ?? baseline?.total ?? null
      };
    });

    // Calculate overall proficiency (using endline scores)
    const validEndlineScores = competencyPerformance.filter(c => c.endlinePercentage !== null);
    const avgProficiency = validEndlineScores.length > 0
      ? Math.round(validEndlineScores.reduce((sum, c) => sum + (c.endlinePercentage || 0), 0) / validEndlineScores.length)
      : 0;

    // Calculate average learning gain
    const validGains = competencyPerformance.filter(c => c.learningGain !== null);
    const avgLearningGain = validGains.length > 0
      ? Math.round(validGains.reduce((sum, c) => sum + (c.learningGain || 0), 0) / validGains.length)
      : 0;

    return {
      userId: parseInt(userId),
      learnerName,
      competencies: competencyPerformance,
      overallProficiency: avgProficiency,
      averageLearningGain: avgLearningGain
    };
  });

  // Identify skill gaps (competencies below threshold) - using endline scores
  const SKILL_GAP_THRESHOLD = 60; // Below 60% is considered a skill gap

  const skillGaps = learnerSkillMatrix.flatMap(learner =>
    learner.competencies
      .filter(c => c.endlinePercentage !== null && c.endlinePercentage < SKILL_GAP_THRESHOLD)
      .map(c => {
        const currentProf = Math.round(c.endlinePercentage || 0);
        const gapValue = Math.round(SKILL_GAP_THRESHOLD - (c.endlinePercentage || 0));
        return {
          learnerName: learner.learnerName,
          userId: learner.userId,
          competency: c.competency,
          currentProficiency: currentProf,
          targetProficiency: SKILL_GAP_THRESHOLD,
          gap: gapValue,
          severity: (currentProf < 40 ? 'Critical' : currentProf < 50 ? 'High' : 'Medium') as 'Critical' | 'High' | 'Medium'
        };
      })
  ).sort((a, b) => b.gap - a.gap); // Largest gaps first

  // Calculate learning gains for all learner-competency pairs
  const learningGains = learnerSkillMatrix.flatMap(learner =>
    learner.competencies
      .filter(c => c.learningGain !== null && c.baselinePercentage !== null && c.endlinePercentage !== null)
      .map(c => {
        const gain = c.learningGain!;
        let category: 'Excellent' | 'Good' | 'Moderate' | 'Minimal' | 'Declined';

        if (gain >= 20) category = 'Excellent';
        else if (gain >= 10) category = 'Good';
        else if (gain >= 5) category = 'Moderate';
        else if (gain >= 0) category = 'Minimal';
        else category = 'Declined';

        return {
          learnerName: learner.learnerName,
          userId: learner.userId,
          competency: c.competency,
          baselinePercentage: c.baselinePercentage!,
          endlinePercentage: c.endlinePercentage!,
          learningGain: gain,
          gainCategory: category
        };
      })
  ).sort((a, b) => b.learningGain - a.learningGain); // Highest gains first

  // Competency gap summary (which competencies have the most learners struggling) - using endline scores
  const competencyGapSummary = allCompetencies.map(comp => {
    const learnersWithData = learnerSkillMatrix.filter(l =>
      l.competencies.find(c => c.competency === comp && c.endlinePercentage !== null)
    );

    const learnersWithGap = learnersWithData.filter(l => {
      const competency = l.competencies.find(c => c.competency === comp);
      return competency && competency.endlinePercentage !== null && competency.endlinePercentage < SKILL_GAP_THRESHOLD;
    });

    const avgProficiency = learnersWithData.length > 0
      ? Math.round(
          learnersWithData
            .map(l => l.competencies.find(c => c.competency === comp)?.endlinePercentage || 0)
            .reduce((sum, p) => sum + p, 0) / learnersWithData.length
        )
      : 0;

    const gapPercentage = learnersWithData.length > 0
      ? Math.round((learnersWithGap.length / learnersWithData.length) * 100)
      : 0;

    // Determine status based on gap percentage
    let status: 'Critical' | 'Needs Attention' | 'Monitor' | 'On Track';
    if (gapPercentage >= 50) {
      status = 'Critical';
    } else if (gapPercentage >= 30) {
      status = 'Needs Attention';
    } else if (gapPercentage > 0) {
      status = 'Monitor';
    } else {
      status = 'On Track';
    }

    return {
      competency: comp,
      totalLearners: learnersWithData.length,
      learnersWithGap: learnersWithGap.length,
      gapPercentage,
      avgProficiency,
      needsAttention: learnersWithGap.length > 0, // Keep for backward compatibility
      status
    };
  })
  .filter(c => c.totalLearners > 0)
  .sort((a, b) => b.gapPercentage - a.gapPercentage);

  return {
    learners: uniqueLearners,
    overdue: risks.length,
    programs: activePrograms,
    deadlines: upcomingDeadlines,
    learningDonut,
    programDonut,
    completionRates,
    training: {
      labels: ['Enrolled', 'Started', 'Completed'],
      values: [validEnrollments.length, started + completed, completed]
    },
    enrollmentsMonthly,
    programHealth,
    risks,
    deadlinesRows,
    competencies,
    courses,
    competencyWeights,
    prePost,
    csat: avgCSAT,
    effectivenessByCourse,
    skillGaps: skillGaps.length > 0 ? skillGaps : undefined,
    competencyGapSummary: competencyGapSummary.length > 0 ? competencyGapSummary : undefined,
    learnerSkillMatrix: learnerSkillMatrix.length > 0 ? learnerSkillMatrix : undefined,
    learningGains: learningGains.length > 0 ? learningGains : undefined,
    rawRecords: {
      allEnrollments: validEnrollments,
      lessonPlans: lessonPlansData,
      tags: tagsData,
      uniqueLearnerRecords,
      overdueRecords: risks.map(r => {
        const enrollment = validEnrollments.find(e =>
          e.LearnerName === r.learner && e.LearningModule === r.course
        );
        return enrollment;
      }).filter(Boolean),
      activeProgramRecords,
      upcomingDeadlineRecords,
      completedRecords,
      startedRecords,
      assignedRecords,
      programCohortsFullyCompleted: activeProgramRecords.filter(p => p.CompletedEnrollments === p.TotalEnrollments),
      programCohortsInProgress: activeProgramRecords.filter(p => p.CompletedEnrollments < p.TotalEnrollments && (p.StartedEnrollments > 0 || p.CompletedEnrollments > 0)),
      programCohortsNotStarted: activeProgramRecords.filter(p => p.StartedEnrollments === 0 && p.CompletedEnrollments === 0)
    }
  };
};
